import { Link } from 'react-router-dom';
import { FiList, FiUsers, FiBookOpen, FiSearch, FiArrowRight } from 'react-icons/fi';

/**
 * Página informativa de Comparativa de Mandamientos (para acceder desde el Home)
 */
export default function MandamientosInfo() {
  return (
    <div className="min-h-full bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl shadow-xl p-8 mb-8 text-white">
          <div className="flex items-center gap-4 mb-4">
            <FiList className="w-12 h-12" />
            <h1 className="text-4xl font-bold">
              Comparativa de Mandamientos
            </h1>
          </div>
          <p className="text-xl text-purple-50 mb-6">
            Comparación detallada de los mandamientos en diferentes tradiciones y perspectivas rabínicas.
          </p>
          
          <Link
            to="/mandamientos"
            className="inline-flex items-center gap-2 px-6 py-3 bg-white text-purple-600 rounded-lg font-semibold hover:bg-purple-50 transition-colors shadow-lg"
          >
            Ver Todos los Mandamientos
            <FiArrowRight className="w-5 h-5" />
          </Link>
        </div>

        {/* Características */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-6">
            <FiList className="w-8 h-8 text-purple-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Comparación Lado a Lado
            </h3>
            <p className="text-gray-600">
              Compara los 613 mandamientos según diferentes perspectivas rabínicas y académicas.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <FiBookOpen className="w-8 h-8 text-purple-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Análisis Histórico
            </h3>
            <p className="text-gray-600">
              Explora el contexto histórico y cultural de cada mandamiento a través del tiempo.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <FiUsers className="w-8 h-8 text-purple-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Contexto Cultural
            </h3>
            <p className="text-gray-600">
              Comprende cómo diferentes comunidades interpretan y aplican cada mandamiento.
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-6">
            <FiSearch className="w-8 h-8 text-purple-600 mb-3" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Referencias Bíblicas
            </h3>
            <p className="text-gray-600">
              Accede directamente a los pasajes bíblicos que fundamentan cada mandamiento.
            </p>
          </div>
        </div>

        {/* Perspectivas Disponibles */}
        <div className="bg-white rounded-xl shadow-md p-8 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Perspectivas Disponibles
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div className="border border-purple-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <h4 className="font-semibold text-purple-900 mb-1">Rambam (Maimónides)</h4>
              <p className="text-sm text-gray-600">Sefer HaMitzvot - Listado clásico de los 613 mandamientos</p>
            </div>

            <div className="border border-purple-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <h4 className="font-semibold text-purple-900 mb-1">Ramban (Nahmánides)</h4>
              <p className="text-sm text-gray-600">Hasagot - Comentarios y diferencias con Rambam</p>
            </div>

            <div className="border border-purple-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <h4 className="font-semibold text-purple-900 mb-1">SEMAG</h4>
              <p className="text-sm text-gray-600">Sefer Mitzvot Gadol - Organización por categorías</p>
            </div>

            <div className="border border-purple-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <h4 className="font-semibold text-purple-900 mb-1">Sefer HaChinuch</h4>
              <p className="text-sm text-gray-600">Enfoque educativo y razones de los mandamientos</p>
            </div>

            <div className="border border-purple-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <h4 className="font-semibold text-purple-900 mb-1">Ibn Ezra</h4>
              <p className="text-sm text-gray-600">Perspectiva filosófica y racionalista</p>
            </div>

            <div className="border border-purple-200 rounded-lg p-4 hover:shadow-md transition-shadow">
              <h4 className="font-semibold text-purple-900 mb-1">Perspectiva Personal</h4>
              <p className="text-sm text-gray-600">Crea tu propia lista personalizada (usuarios registrados)</p>
            </div>
          </div>
        </div>

        {/* Funcionalidades */}
        <div className="bg-white rounded-xl shadow-md p-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Funcionalidades Disponibles
          </h2>
          
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                ✓
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Listado Completo de los 613 Mandamientos</h4>
                <p className="text-gray-600 text-sm">Navega por el listado completo con filtros y búsqueda avanzada.</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                ✓
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Vista Comparativa</h4>
                <p className="text-gray-600 text-sm">Compara el mismo mandamiento en hasta 4 perspectivas simultáneas.</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                ✓
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Categorización y Filtros</h4>
                <p className="text-gray-600 text-sm">Filtra por tipo (positivos/negativos), categoría y aplicabilidad actual.</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                ✓
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Referencias a la Torá</h4>
                <p className="text-gray-600 text-sm">Enlaces directos a los pasajes bíblicos de cada mandamiento.</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                ✓
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Raíces Hebreas</h4>
                <p className="text-gray-600 text-sm">Análisis de las palabras originales y su significado profundo.</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                ✓
              </div>
              <div>
                <h4 className="font-semibold text-gray-900">Perspectivas Personalizadas (Usuarios Registrados)</h4>
                <p className="text-gray-600 text-sm">Crea y comparte tus propias listas y notas sobre los mandamientos.</p>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Final */}
        <div className="mt-8 text-center">
          <Link
            to="/mandamientos"
            className="inline-flex items-center gap-2 px-8 py-4 bg-purple-600 text-white rounded-lg font-semibold text-lg hover:bg-purple-700 transition-colors shadow-lg"
          >
            <FiList className="w-5 h-5" />
            Ver Mandamientos
          </Link>
        </div>
      </div>
    </div>
  );
}






